export type GlassRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  radii: number[];
};

export type GlassMap = { source: string; width: number; height: number };

export const glassRefractionScale = 128;
const maps = new Map<string, GlassMap>();

export function supportsGlassRefraction() {
  return typeof navigator !== "undefined" &&
    /(?:Chrome|Chromium|Edg)\//.test(navigator.userAgent);
}

export function readGlassRect(element: HTMLElement, x = 0, y = 0): GlassRect {
  const { width, height } = element.getBoundingClientRect();
  const style = getComputedStyle(element);
  const radii = [
    style.borderTopLeftRadius,
    style.borderTopRightRadius,
    style.borderBottomRightRadius,
    style.borderBottomLeftRadius,
  ].map((value) => Math.min(
    value.endsWith("%")
      ? parseFloat(value) * Math.min(width, height) / 100
      : parseFloat(value) || 0,
    width / 2,
    height / 2,
  ));
  return { x, y, width, height, radii };
}

function rectangleDistance(x: number, y: number, rect: GlassRect) {
  const px = x - rect.x - rect.width / 2;
  const py = y - rect.y - rect.height / 2;
  const radius = rect.radii[py < 0 ? (px < 0 ? 0 : 1) : px < 0 ? 3 : 2]!;
  const qx = Math.abs(px) - rect.width / 2 + radius;
  const qy = Math.abs(py) - rect.height / 2 + radius;
  return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) +
    Math.min(Math.max(qx, qy), 0) - radius;
}

// Polynomial smooth union gives nearby surfaces one continuous contour.
function unionDistance(x: number, y: number, rects: GlassRect[], fusion: number) {
  let distance = Infinity;
  for (const rect of rects) {
    const next = rectangleDistance(x, y, rect);
    const blend = fusion ? Math.max(fusion - Math.abs(distance - next), 0) / fusion : 0;
    distance = Math.min(distance, next) - blend * blend * fusion / 4;
  }
  return distance;
}

export function createGlassMap(
  width: number,
  height: number,
  rects: GlassRect[],
  fusion = 0,
  masked = false,
): GlassMap | undefined {
  if (width < 1 || height < 1 || !rects.length) return;
  const key = JSON.stringify([width, height, rects, fusion, masked]);
  const cached = maps.get(key);
  if (cached) return cached;
  // Moving groups have a bounded pixel budget; the compositor scales the map.
  const ratio = masked
    ? Math.min(1, 640 / width, 320 / height, Math.sqrt(65_536 / (width * height)))
    : Math.min(1, 768 / width, 512 / height);
  const w = Math.max(2, Math.round(width * ratio));
  const h = Math.max(2, Math.round(height * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const context = canvas.getContext("2d");
  if (!context) return;
  const pixels = context.createImageData(w, h);
  const field = new Float32Array(w * h);
  const stepX = width / w;
  const stepY = height / h;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      field[y * w + x] = unionDistance(
        (x + 0.5) * stepX, (y + 0.5) * stepY, rects, fusion,
      );
    }
  }
  const rim = Math.min(40, ...rects.map((rect) => Math.min(rect.width, rect.height) * 0.4));
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const index = y * w + x;
      const offset = index * 4;
      const distance = field[index]!;
      pixels.data[offset] = 128;
      pixels.data[offset + 1] = 128;
      pixels.data[offset + 3] = masked
        ? Math.max(0, Math.min(1, 0.5 - distance / Math.max(stepX, stepY))) * 255
        : 255;
      if (distance >= 0 || distance <= -rim) continue;
      const dx = (field[y * w + Math.min(w - 1, x + 1)]! -
        field[y * w + Math.max(0, x - 1)]!) / stepX;
      const dy = (field[Math.min(h - 1, y + 1) * w + x]! -
        field[Math.max(0, y - 1) * w + x]!) / stepY;
      const length = Math.hypot(dx, dy) || 1;
      const nx = dx / length;
      const ny = dy / length;
      const depth = -distance / rim;
      const slope = 1 - depth;
      const surface = Math.sqrt(1 - slope * slope);
      const incident = Math.atan2(slope, Math.max(0.08, surface));
      const transmitted = Math.asin(Math.sin(incident) / 1.5);
      const bend = Math.tan(incident - transmitted) * (32 + surface * 28) * rim / 40;
      const fresnel = 0.04 + 0.96 * Math.pow(1 - Math.cos(incident), 5);
      const light = Math.max(0, -nx * 0.55 - ny * 0.8);
      pixels.data[offset] = 128 - nx * bend * 255 / glassRefractionScale;
      pixels.data[offset + 1] = 128 - ny * bend * 255 / glassRefractionScale;
      pixels.data[offset + 2] =
        (fresnel * 0.75 + light * 0.25) * Math.pow(1 - depth, 1.5) * 255;
    }
  }
  context.putImageData(pixels, 0, 0);
  const map = { source: canvas.toDataURL(), width, height };
  if (maps.size >= 16) maps.delete(maps.keys().next().value!);
  maps.set(key, map);
  return map;
}
